import { Link } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Metric, RiskBadge, StatusBadge, formatCurrency } from "@odingard/design-system";
import { LoadingState } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { CaiError } from "../../components/CaiError";

export function CaiDashboard() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(
    async () => {
      const [dash, contracts] = await Promise.all([api.dashboard(orgId), api.contracts(orgId)]);
      return { metrics: dash.metrics, recent: contracts.contracts.slice(0, 5) };
    },
    [orgId],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Contract AI</h1>
          <p>Portfolio overview and AI activity.</p>
        </div>
        <div className="ods-row">
          <Link to="/studio/contract-ai/upload">
            <Button size="sm">Upload contract</Button>
          </Link>
          <Link to="/studio/contract-ai/ask">
            <Button size="sm" variant="secondary">
              Ask your contracts
            </Button>
          </Link>
        </div>
      </div>

      {state.loading && <LoadingState label="Loading dashboard…" />}
      {state.error && <CaiError error={state.error} onRetry={state.reload} />}
      {state.data && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={state.data.metrics.totalContracts} label="Contracts" />
            </div>
            <div className="metric-card">
              <Metric value={formatCurrency(state.data.metrics.contractValue, "usd")} label="Portfolio value" />
            </div>
            <div className="metric-card">
              <Metric value={state.data.metrics.reviewQueue} label="Awaiting review" tone="var(--ods-color-warning)" />
            </div>
            <div className="metric-card">
              <Metric value={state.data.metrics.highRiskClauses} label="Clauses carrying high risk" tone="var(--ods-color-danger)" />
            </div>
          </div>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={state.data.metrics.upcomingRenewals} label="Upcoming renewals" />
            </div>
            <div className="metric-card">
              <Metric value={state.data.metrics.overdueObligations} label="Overdue obligations" tone="var(--ods-color-danger)" />
            </div>
            <div className="metric-card">
              <Metric value={`${state.data.metrics.roiSummary.hoursSaved}h`} label="Hours saved" tone="var(--ods-color-success)" />
            </div>
            <div className="metric-card">
              <Metric value={formatCurrency(state.data.metrics.roiSummary.dollarsSaved, "usd")} label="Est. savings" tone="var(--ods-color-success)" />
            </div>
          </div>

          <Card>
            <CardHeader
              title="Recent contracts"
              action={
                <Link to="/studio/contract-ai/repository">
                  <Button size="sm" variant="ghost">
                    View all
                  </Button>
                </Link>
              }
            />
            <CardBody>
              <div className="ods-stack">
                {state.data.recent.map((c) => (
                  <Link
                    key={c.id}
                    to={`/studio/contract-ai/contracts/${c.id}`}
                    className="ods-row"
                    style={{ justifyContent: "space-between", textDecoration: "none", color: "inherit", padding: "10px 0", borderBottom: "1px solid var(--ods-color-line)" }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{c.title}</div>
                      <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{c.counterparty}</span>
                    </div>
                    <div className="ods-row">
                      <RiskBadge level={c.riskLevel} />
                      <StatusBadge status={c.status} />
                    </div>
                  </Link>
                ))}
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
}
